// src/components/product/ProductSpecificationSection.tsx

import { useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

import { ProductUpsertRequest } from '@apis/product';

type ProductSpecification = ProductUpsertRequest['specification'];
type DataCapacity = ProductSpecification['dataCapacity'];

type Country = {
  countryId: number;
  englishName: string;
};

export interface ProductSpecificationSectionProps {
  specification: ProductSpecification;
  coverages: Country[];
  allCountries: Country[];
  onChangeSpecification: (
    name: keyof ProductSpecification,
    value: string | number | boolean | null,
  ) => void;
  onChangeSpecificationData: (
    name: keyof DataCapacity,
    value: string | number,
  ) => void;
  onAddCoverage: (country: Country) => void;
  onDeleteCoverage: (countryId: number) => void;
}

const DATA_UNITS = ['MB', 'GB', 'TB'] as const;
const REFRESH_UNITS = ['DAYS', 'WEEKS', 'MONTHS', 'YEARS'] as const;

const REFRESH_UNIT_LABELS: Record<(typeof REFRESH_UNITS)[number], string> = {
  DAYS: 'Days',
  WEEKS: 'Weeks',
  MONTHS: 'Months',
  YEARS: 'Years',
};

const REFRESH_UNIT_SINGULAR_LABELS: Record<(typeof REFRESH_UNITS)[number], string> = {
  DAYS: 'day',
  WEEKS: 'week',
  MONTHS: 'month',
  YEARS: 'year',
};

const dataPlanPanelSx = {
  borderRadius: 1,
  border: '1px solid',
  borderColor: 'rgba(0, 0, 0, 0.06)',
  bgcolor: 'action.hover',
  p: 2,
  minWidth: 0,
  height: '100%',
} as const;

const dataPlanPanelTitleSx = {
  mb: 1.25,
  fontWeight: 800,
  color: 'text.primary',
} as const;

const segmentedChoiceGroupSx = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: 0.75,
} as const;

const segmentedChoiceButtonSx = {
  minWidth: 0,
  minHeight: 34,
  px: 1,
  textTransform: 'none',
  fontWeight: 800,
  lineHeight: 1.2,
  borderColor: 'divider',
} as const;

const softDividerSx = {
  my: 1.5,
  borderColor: 'rgba(0, 0, 0, 0.06)',
} as const;

const sectionDividerSx = {
  my: 3,
  borderColor: 'rgba(0, 0, 0, 0.06)',
} as const;

/**
 * ProductSpecificationSection
 *
 * Edits product "specification" fields:
 * 1) Data plan (unlimited / fixed quota, FUP and refresh)
 * 2) Validity and voice support
 * 3) Coverage countries (add/remove via Autocomplete + chips)
 *
 * UI safety guards:
 * - Deduplicates coverages by countryId to keep list rendering stable (unique React keys).
 * - Hides the refresh unit Select when refresh is disabled, because refreshPeriodUnit can be
 *   "NEVER" which is not a valid Select option.
 * - Keeps Autocomplete "value" controlled as null so the selection does not remain displayed
 *   after adding a country; only the query text is controlled.
 */
const ProductSpecificationSection = ({
  specification,
  coverages,
  allCountries,
  onChangeSpecification,
  onChangeSpecificationData,
  onAddCoverage,
  onDeleteCoverage,
}: ProductSpecificationSectionProps) => {
  const isUnlimited = specification.isUnlimitedData;
  const hasRefresh = specification.dataCapacity.refreshPeriodUnit !== 'NEVER';
  const refreshPeriodValue = Number(specification.dataCapacity.refreshPeriodValue || 0);
  const refreshPeriodUnit = specification.dataCapacity.refreshPeriodUnit as (typeof REFRESH_UNITS)[number];
  const refreshPeriodUnitText =
    refreshPeriodValue === 1
      ? REFRESH_UNIT_SINGULAR_LABELS[refreshPeriodUnit]
      : REFRESH_UNIT_LABELS[refreshPeriodUnit]?.toLowerCase();
  const dataLimitDescription =
    hasRefresh && refreshPeriodValue > 0 && refreshPeriodUnitText
      ? `Resets every ${refreshPeriodValue} ${refreshPeriodUnitText}.`
      : 'Applies across full validity.';
  const afterLimitDescription = specification.isFup
    ? 'Reduce speed after limit.'
    : 'Cut off data after limit.';

  // Controls the Autocomplete input text only (not the selected option).
  const [countryQuery, setCountryQuery] = useState('');

  /**
   * Deduplicate coverages by countryId (defensive).
   * Using a Map avoids React duplicate-key warnings and makes the chip list deterministic
   * even if upstream data contains duplicates.
   *
   * NOTE:
   * - We intentionally avoid spreading Map.values() to keep compatibility with lower TS targets.
   */
  const sortedCoverages = useMemo(() => {
    const byId = new Map<number, Country>();

    for (const c of coverages) {
      if (!c?.countryId) continue;
      byId.set(c.countryId, c);
    }

    return Array.from(byId.values()).sort((a, b) =>
      a.englishName.localeCompare(b.englishName),
    );
  }, [coverages]);

  /**
   * Countries available to add:
   * allCountries - selectedCoverages (deduped).
   */
  const availableCountries = useMemo(() => {
    const selectedIds = new Set(sortedCoverages.map((c) => c.countryId));

    return allCountries
      .filter((c) => !selectedIds.has(c.countryId))
      .sort((a, b) => a.englishName.localeCompare(b.englishName));
  }, [allCountries, sortedCoverages]);

  const handleToggleUnlimited = (checked: boolean) => {
    onChangeSpecification('isUnlimitedData', checked);

    // When unlimited is enabled, reset quota/refresh fields to a neutral state.
    if (checked) {
      onChangeSpecificationData('quantity', 0);
      onChangeSpecificationData('unit', 'MB');
      onChangeSpecificationData('refreshPeriodUnit', 'NEVER');
      onChangeSpecificationData('refreshPeriodValue', 0);
    }
  };

  const handleToggleRefresh = (enabled: boolean) => {
    if (!enabled) {
      onChangeSpecificationData('refreshPeriodUnit', 'NEVER');
      onChangeSpecificationData('refreshPeriodValue', 0);
      return;
    }

    // When enabling refresh, initialize to a sensible default.
    onChangeSpecificationData('refreshPeriodUnit', 'DAYS');
    onChangeSpecificationData('refreshPeriodValue', 1);
  };

  const handleChangeQuantity = (raw: string) => {
    const val = Number(raw || 0);
    if (val < 0) return;
    onChangeSpecificationData('quantity', val);
  };

  const handleChangeDayCount = (raw: string) => {
    onChangeSpecification('dayCount', Number(raw || 0));
  };

  const handleChangeRefreshValue = (raw: string) => {
    const val = Number(raw || 0);
    if (val < 0) return;
    onChangeSpecificationData('refreshPeriodValue', val);
  };

  return (
    <Card
      elevation={0}
      sx={(theme) => ({
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        mb: 3,
      })}
    >
      <CardHeader
        title={
          <Typography variant="h6" fontWeight={700}>
            Specification
          </Typography>
        }
        subheader={
          <Typography variant="body2" color="text.secondary">
            Configure data plan, validity, voice support and country coverage.
          </Typography>
        }
      />

      <CardContent>
        <Stack spacing={0}>
          {/* 1) Data plan ---------------------------------------------------- */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
              1. Data Plan
            </Typography>

            <FormControlLabel
              control={
                <Switch
                  checked={isUnlimited}
                  onChange={(e) => handleToggleUnlimited(e.target.checked)}
                />
              }
              label="Unlimited data"
            />

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {isUnlimited
                ? 'Data limit, FUP and refresh cycle are not used for unlimited products.'
                : 'Use a fixed quota, optional FUP behavior and an optional refresh cycle.'}
            </Typography>

            {!isUnlimited ? (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '1.1fr 1.2fr 1.1fr' },
                  gap: 2,
                  mt: 2,
                }}
              >
                {/* Refresh cycle */}
                <Box sx={dataPlanPanelSx}>
                  <Typography variant="subtitle2" sx={dataPlanPanelTitleSx}>
                    Refresh
                  </Typography>

                  <Box sx={segmentedChoiceGroupSx} role="group" aria-label="Refresh cycle">
                    <Button
                      variant={!hasRefresh ? 'contained' : 'outlined'}
                      color={!hasRefresh ? 'primary' : 'inherit'}
                      onClick={() => handleToggleRefresh(false)}
                      sx={segmentedChoiceButtonSx}
                    >
                      No reset
                    </Button>
                    <Button
                      variant={hasRefresh ? 'contained' : 'outlined'}
                      color={hasRefresh ? 'primary' : 'inherit'}
                      onClick={() => handleToggleRefresh(true)}
                      sx={segmentedChoiceButtonSx}
                    >
                      Reset
                    </Button>
                  </Box>

                  {hasRefresh ? (
                    <Box>
                      <Divider sx={softDividerSx} />

                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={1}
                        sx={{ alignItems: { xs: 'stretch', sm: 'flex-start' } }}
                      >
                        <TextField
                          label="Every"
                          size="small"
                          type="number"
                          InputProps={{ inputProps: { min: 0 } }}
                          value={specification.dataCapacity.refreshPeriodValue}
                          onChange={(e) => handleChangeRefreshValue(e.target.value)}
                          sx={{ width: { xs: '100%', sm: 100 } }}
                        />

                        <FormControl size="small" sx={{ minWidth: { xs: 0, sm: 130 } }}>
                          <InputLabel id="refresh-unit-label">Unit</InputLabel>
                          <Select
                            labelId="refresh-unit-label"
                            label="Unit"
                            value={specification.dataCapacity.refreshPeriodUnit}
                            onChange={(e) =>
                              onChangeSpecificationData(
                                'refreshPeriodUnit',
                                e.target.value as string,
                              )
                            }
                          >
                            {REFRESH_UNITS.map((unit) => (
                              <MenuItem key={unit} value={unit}>
                                {REFRESH_UNIT_LABELS[unit]}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Stack>
                    </Box>
                  ) : (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', mt: 0.75 }}
                    >
                      Fixed quota for the entire validity period.
                    </Typography>
                  )}
                </Box>

                {/* Data limit */}
                <Box sx={dataPlanPanelSx}>
                  <Typography variant="subtitle2" sx={dataPlanPanelTitleSx}>
                    Data Limit
                  </Typography>

                  <Stack direction="row" spacing={1}>
                    <TextField
                      size="small"
                      type="number"
                      InputProps={{ inputProps: { min: 0 } }}
                      value={specification.dataCapacity.quantity}
                      onChange={(e) => handleChangeQuantity(e.target.value)}
                      sx={{ width: 120 }}
                    />

                    <FormControl size="small" sx={{ minWidth: 80 }}>
                      <InputLabel id="unit-label">Unit</InputLabel>
                      <Select
                        labelId="unit-label"
                        label="Unit"
                        value={specification.dataCapacity.unit}
                        onChange={(e) =>
                          onChangeSpecificationData('unit', e.target.value as string)
                        }
                      >
                        {DATA_UNITS.map((unit) => (
                          <MenuItem key={unit} value={unit}>
                            {unit}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Stack>

                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mt: 0.75 }}
                  >
                    {dataLimitDescription}
                  </Typography>
                </Box>

                {/* FUP */}
                <Box sx={dataPlanPanelSx}>
                  <Typography variant="subtitle2" sx={dataPlanPanelTitleSx}>
                    After Limit
                  </Typography>

                  <Box sx={segmentedChoiceGroupSx} role="group" aria-label="After data limit">
                    <Button
                      variant={!specification.isFup ? 'contained' : 'outlined'}
                      color={!specification.isFup ? 'primary' : 'inherit'}
                      onClick={() => onChangeSpecification('isFup', false)}
                      sx={segmentedChoiceButtonSx}
                    >
                      Cut-off
                    </Button>
                    <Button
                      variant={specification.isFup ? 'contained' : 'outlined'}
                      color={specification.isFup ? 'primary' : 'inherit'}
                      onClick={() => onChangeSpecification('isFup', true)}
                      sx={segmentedChoiceButtonSx}
                    >
                      FUP
                    </Button>
                  </Box>

                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mt: 0.75 }}
                  >
                    {afterLimitDescription}
                  </Typography>
                </Box>
              </Box>
            ) : null}
          </Box>

          <Divider sx={sectionDividerSx} />

          {/* 2) Validity / voice --------------------------------------------- */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'minmax(180px, 240px) 1fr' },
              columnGap: 3,
              rowGap: 2,
            }}
          >
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                2. Validity &amp; Voice
              </Typography>

              <TextField
                label="Days"
                size="small"
                type="number"
                InputProps={{ inputProps: { min: 0 } }}
                value={specification.dayCount}
                onChange={(e) => handleChangeDayCount(e.target.value)}
                fullWidth
                helperText="Number of days after activation."
              />
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                Voice
              </Typography>

              <FormControlLabel
                control={
                  <Switch
                    checked={specification.isVoiceCallSupported}
                    onChange={(e) =>
                      onChangeSpecification('isVoiceCallSupported', e.target.checked)
                    }
                  />
                }
                label={
                  specification.isVoiceCallSupported
                    ? 'Voice calls included'
                    : 'Data only'
                }
              />

              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Enable if the plan includes voice minutes.
              </Typography>
            </Box>
          </Box>

          <Divider sx={sectionDividerSx} />

          {/* 3) Coverage ------------------------------------------------------ */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
              3. Coverage
            </Typography>

            <Box
              sx={{
                display: 'flex',
                alignItems: { xs: 'stretch', sm: 'flex-start' },
                gap: { xs: 0.75, sm: 1.5 },
                flexDirection: { xs: 'column', sm: 'row' },
                mb: 2,
              }}
            >
              <Autocomplete
                value={null}
                inputValue={countryQuery}
                onInputChange={(_, next, reason) => {
                  // Keep the input fully controlled, but avoid "re-applying" a value after selection.
                  // - "input": user typing
                  // - "clear"/"reset": MUI internally clears the input (e.g., after selection or ESC)
                  if (reason === 'input') setCountryQuery(next);
                  if (reason === 'clear' || reason === 'reset') setCountryQuery('');
                }}
                options={availableCountries}
                getOptionLabel={(opt) => opt.englishName}
                isOptionEqualToValue={(opt, val) => opt.countryId === val.countryId}
                onChange={(_, selected) => {
                  if (!selected) return;
                  onAddCoverage(selected);
                  setCountryQuery('');
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Add country"
                    placeholder="Search and select a country"
                    size="small"
                  />
                )}
                sx={{ width: { xs: '100%', sm: 360 } }}
              />

              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ pt: { xs: 0, sm: 1.25 }, minWidth: 0 }}
              >
                Select countries where this product can be used.
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                columnGap: 1,
                rowGap: 1.2,
                minWidth: 0,
              }}
            >
              {sortedCoverages.map((c) => (
                <Chip
                  key={c.countryId}
                  label={c.englishName}
                  onDelete={() => onDeleteCoverage(c.countryId)}
                  sx={{ maxWidth: 220 }}
                />
              ))}

              {sortedCoverages.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No countries selected.
                </Typography>
              ) : null}
            </Box>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default ProductSpecificationSection;
